function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseConfiguration(json: string): Record<string, unknown> {
  const value: unknown = JSON.parse(json);
  if (!isObject(value)) throw new Error('Expected a JSON object.');
  // JSON.parse discards identical duplicate keys. Check the original tokens before editing any document.
  const tokens = [...json.matchAll(/"(?:\\.|[^"\\])*"|[{}[\]:,]/g)];
  const scopes: (Set<string> | null)[] = [];
  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index][0];
    if (token === '{') scopes.push(new Set());
    else if (token === '[') scopes.push(null);
    else if (token === '}' || token === ']') scopes.pop();
    else if (token.startsWith('"') && tokens[index + 1]?.[0] === ':') {
      const key = (JSON.parse(token) as string).toLowerCase();
      const scope = scopes.at(-1);
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
        throw new Error('Unsupported configuration key.');
      }
      if (!scope || scope.has(key)) throw new Error('Duplicate configuration keys.');
      scope.add(key);
      if (scopes.length === 1 && key === 'tpmfailoverthresholdpercent'
        && !/^\s*\d+\s*[,}]/.test(json.slice(tokens[index + 1].index + 1))) {
        throw new Error('TPM failover must be a JSON integer.');
      }
    }
  }
  return value;
}

// The server merges property names and resource keys case-insensitively, unlike the generic preview.
function normalizeKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeKeys);
  if (!isObject(value)) return value;
  const normalized: Record<string, unknown> = Object.create(null);
  for (const [key, item] of Object.entries(value)) {
    const normalizedKey = key.toLowerCase();
    if (normalizedKey === '__proto__' || normalizedKey === 'constructor' || normalizedKey === 'prototype') {
      throw new Error('Unsupported configuration key.');
    }
    if (Object.hasOwn(normalized, normalizedKey)) throw new Error('Duplicate configuration keys.');
    normalized[normalizedKey] = normalizeKeys(item);
  }
  return normalized;
}

function mergeSaved(base: unknown, override: unknown): unknown {
  if (!isObject(base) || !isObject(override)) return override;
  const merged: Record<string, unknown> = Object.create(null);
  for (const key of new Set([...Object.keys(base), ...Object.keys(override)])) {
    const baseValue = Object.hasOwn(base, key) ? base[key] : undefined;
    merged[key] = Object.hasOwn(override, key) ? mergeSaved(baseValue, override[key]) : baseValue;
  }
  return merged;
}

export function savedResources(baseJson: string, environmentJson: string) {
  try {
    const base: unknown = normalizeKeys(parseConfiguration(baseJson));
    const override: unknown = normalizeKeys(parseConfiguration(environmentJson));
    if (!isObject(base) || !isObject(override)) throw new Error('Expected JSON objects.');
    const merged = mergeSaved(base, override);
    if (!isObject(merged)) throw new Error('Expected merged options.');
    if (merged.enabled !== undefined && typeof merged.enabled !== 'boolean') throw new Error('Invalid Enabled setting.');
    const resources: unknown = merged.resources === undefined ? Object.create(null) : merged.resources;
    if (!isObject(resources)) throw new Error('Invalid Resources setting.');
    const accounts = Object.entries(resources).map(([key, resource]) => {
      if (!isObject(resource) || typeof resource.resourceid !== 'string' || !resource.resourceid.trim()
        || (resource.enabled !== undefined && typeof resource.enabled !== 'boolean')) {
        throw new Error('Invalid resource settings.');
      }
      // Deliberately project only account IDs and Enabled; never render arbitrary configuration fields.
      return { key, resourceId: resource.resourceid, enabled: resource.enabled !== false };
    });
    return { enabled: merged.enabled === true, accounts, error: null };
  } catch {
    return { enabled: false, accounts: [], error: 'Cannot confirm the saved resources. Save valid JSON objects with valid Resources and Enabled settings first.' };
  }
}

const thresholdProperty = 'TpmFailoverThresholdPercent';

function thresholdKey(value: Record<string, unknown>): string | undefined {
  return Object.keys(value).find((key) => key.toLowerCase() === thresholdProperty.toLowerCase());
}

function validPercent(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 100;
}

function thresholdValue(value: Record<string, unknown>): number | undefined {
  const key = thresholdKey(value);
  if (key === undefined) return undefined;
  const percent = value[key];
  if (!validPercent(percent)) throw new Error('TPM failover must be a whole percentage from 1 to 100.');
  return percent;
}

export interface TpmFailoverThreshold {
  percent: number | null;
  source: 'base' | 'environment' | 'default' | null;
  error: string | null;
}

export function readTpmFailoverThreshold(baseJson: string, environmentJson: string): TpmFailoverThreshold {
  try {
    const base = thresholdValue(parseConfiguration(baseJson));
    const environment = thresholdValue(parseConfiguration(environmentJson));
    return {
      percent: environment ?? base ?? 100,
      source: environment !== undefined ? 'environment' : base !== undefined ? 'base' : 'default',
      error: null,
    };
  } catch {
    return {
      percent: null,
      source: null,
      error: 'Use valid JSON objects with unique keys and a whole TPM failover percentage from 1 to 100. The control has not changed your JSON.',
    };
  }
}

export function stageTpmFailoverThreshold(json: string, percent: number | null): string {
  if (percent !== null && !validPercent(percent)) throw new Error('TPM failover must be a whole percentage from 1 to 100.');
  const value = parseConfiguration(json);
  const key = thresholdKey(value);
  thresholdValue(value);
  if (key === undefined) {
    if (percent === null) return json;
    const end = json.lastIndexOf('}');
    const prefix = json.slice(0, end).trimEnd();
    const newline = json.match(/\r\n|\n|\r/)?.[0] ?? '\n';
    const separator = Object.keys(value).length ? `,${newline}  ` : '';
    return `${prefix}${separator}"${thresholdProperty}": ${percent}${json.slice(prefix.length)}`;
  }
  if (value[key] === percent) return json;
  // Splice only the root property so unrelated values (including large numeric literals) stay intact.
  const tokens = [...json.matchAll(/"(?:\\.|[^"\\])*"|[{}[\]:,]/g)];
  let depth = 0;
  let commaBefore = -1;
  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index][0];
    if (token === '{' || token === '[') depth += 1;
    else if (token === '}' || token === ']') depth -= 1;
    else if (depth === 1 && token === ',') commaBefore = tokens[index].index;
    else if (depth === 1 && token.startsWith('"') && tokens[index + 1]?.[0] === ':' && JSON.parse(token) === key) {
      const start = tokens[index + 1].index + 1;
      const number = /^\s*(-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?)/.exec(json.slice(start));
      if (!number) break;
      const end = start + number[0].length;
      if (percent !== null) {
        return json.slice(0, end - number[1].length) + String(percent) + json.slice(end);
      }
      const commaAfter = /^\s*,/.exec(json.slice(end));
      if (commaAfter) return json.slice(0, tokens[index].index) + json.slice(end + commaAfter[0].length);
      return json.slice(0, commaBefore >= 0 ? commaBefore : tokens[index].index) + json.slice(end);
    }
  }
  throw new Error('Could not locate the TPM failover setting.');
}
